import { Injectable } from '@angular/core';

export interface BackgroundConfig {
  type: 'solid' | 'gradient' | 'image';
  color: string;
  gradientColor1: string;
  gradientColor2: string;
  gradientAngle: number;
  image: HTMLImageElement | null;
  imageScale: number;
  imagePositionX: number;
  imagePositionY: number;
  imageOpacity: number;
  imageBlur: number;
  // Enhancement properties for background image
  imageBrightness?: number;
  imageContrast?: number;
  imageSaturation?: number;
  imageTemperature?: number; // -100 (cool/blue) to +100 (warm/yellow)
  imageTint?: number; // -100 (green) to +100 (magenta)
  // Levels controls
  imageHighlights?: number;
  imageShadows?: number;
  imageMidtones?: number;
}

export interface ShadowLayerConfig {
  enabled: boolean;
  image: HTMLImageElement | null;
  scale: number;
  offsetX: number;
  offsetY: number;
  opacity: number;
  rotation: number;
  blur?: number;
}

export interface CanvasGuideConfig {
  showGrid: boolean;
  showCenterLines: boolean;
  showRuleOfThirds: boolean;
}

export interface CompositionPreset {
  name: string;
  backgroundColor: string;
  backgroundType: 'solid' | 'gradient';
  gradientColor1?: string;
  gradientColor2?: string;
  gradientAngle?: number;
}

export const COMPOSITION_PRESETS: CompositionPreset[] = [
  { name: 'White', backgroundColor: '#ffffff', backgroundType: 'solid' },
  { name: 'Black', backgroundColor: '#000000', backgroundType: 'solid' },
  { name: 'Gray', backgroundColor: '#808080', backgroundType: 'solid' },
  { name: 'Cream', backgroundColor: '#f5f5dc', backgroundType: 'solid' },
  { name: 'Navy', backgroundColor: '#001f3f', backgroundType: 'solid' },
  { name: 'Sunset', backgroundColor: '#ff6b6b', backgroundType: 'gradient', gradientColor1: '#ff6b6b', gradientColor2: '#feca57', gradientAngle: 135 },
  { name: 'Ocean', backgroundColor: '#667eea', backgroundType: 'gradient', gradientColor1: '#667eea', gradientColor2: '#764ba2', gradientAngle: 135 },
  { name: 'Forest', backgroundColor: '#11998e', backgroundType: 'gradient', gradientColor1: '#11998e', gradientColor2: '#38ef7d', gradientAngle: 135 },
];

@Injectable({
  providedIn: 'root'
})
export class CanvasCompositorService {

  getCompositionPresets(): CompositionPreset[] {
    return COMPOSITION_PRESETS;
  }

  getDefaultBackgroundConfig(): BackgroundConfig {
    return {
      type: 'solid',
      color: '#ffffff',
      gradientColor1: '#ffffff',
      gradientColor2: '#e0e0e0',
      gradientAngle: 180,
      image: null,
      imageScale: 100,
      imagePositionX: 50,
      imagePositionY: 50,
      imageOpacity: 100,
      imageBlur: 0
    };
  }

  getDefaultShadowLayerConfig(): ShadowLayerConfig {
    return {
      enabled: false,
      image: null,
      scale: 100,
      offsetX: 0,
      offsetY: 20,
      opacity: 50,
      rotation: 0,
      blur: 15
    };
  }

  /**
   * Draw background on canvas
   */
  drawBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    background: BackgroundConfig
  ): void {
    if (background.type === 'image' && background.image) {
      ctx.save();

      const scale = background.imageScale / 100;
      const imgWidth = background.image.width * scale;
      const imgHeight = background.image.height * scale;

      const posX = (background.imagePositionX / 100) * width - imgWidth / 2;
      const posY = (background.imagePositionY / 100) * height - imgHeight / 2;

      // Build filter string with all enhancements
      const filters: string[] = [];
      if (background.imageBlur && background.imageBlur > 0) {
        filters.push(`blur(${background.imageBlur}px)`);
      }

      // Calculate effective brightness including levels adjustments
      // Highlights, shadows, midtones modify the base brightness (approximation using CSS filters)
      let effectiveBrightness = background.imageBrightness ?? 100;
      const highlights = background.imageHighlights ?? 0;
      const shadows = background.imageShadows ?? 0;
      const midtones = background.imageMidtones ?? 0;

      // Apply levels as brightness modifiers (simplified approximation)
      // Each level adjustment adds/subtracts from brightness
      effectiveBrightness += (highlights * 0.3) + (midtones * 0.5) + (shadows * 0.2);

      if (effectiveBrightness !== 100) {
        filters.push(`brightness(${effectiveBrightness}%)`);
      }
      if (background.imageContrast && background.imageContrast !== 100) {
        filters.push(`contrast(${background.imageContrast}%)`);
      }
      if (background.imageSaturation && background.imageSaturation !== 100) {
        filters.push(`saturate(${background.imageSaturation}%)`);
      }
      if (filters.length > 0) {
        ctx.filter = filters.join(' ');
      }

      ctx.globalAlpha = background.imageOpacity / 100;

      // If temperature or tint adjustments are needed, draw to a temporary canvas first
      const hasTemperatureOrTint = (background.imageTemperature && background.imageTemperature !== 0) ||
                                    (background.imageTint && background.imageTint !== 0);

      if (hasTemperatureOrTint) {
        // Create temporary canvas for pixel manipulation
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imgWidth;
        tempCanvas.height = imgHeight;
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });

        if (tempCtx) {
          // Draw with filters first
          tempCtx.filter = ctx.filter;
          tempCtx.drawImage(background.image, 0, 0, imgWidth, imgHeight);
          tempCtx.filter = 'none';

          // Apply temperature and tint adjustments at pixel level
          const imageData = tempCtx.getImageData(0, 0, imgWidth, imgHeight);
          const data = imageData.data;

          const temperature = background.imageTemperature ?? 0;
          const tint = background.imageTint ?? 0;

          // Temperature: affects red-blue balance
          // Tint: affects green-magenta balance
          for (let i = 0; i < data.length; i += 4) {
            // Temperature adjustment (warm = more red/yellow, cool = more blue)
            if (temperature !== 0) {
              const tempFactor = temperature / 100; // -1 to +1
              data[i] = Math.min(255, Math.max(0, data[i] + tempFactor * 30)); // Red
              data[i + 2] = Math.min(255, Math.max(0, data[i + 2] - tempFactor * 30)); // Blue
            }

            // Tint adjustment (magenta = more red+blue, green = more green)
            if (tint !== 0) {
              const tintFactor = tint / 100; // -1 to +1
              if (tintFactor > 0) {
                // Add magenta (red + blue, reduce green)
                data[i] = Math.min(255, Math.max(0, data[i] + tintFactor * 20)); // Red
                data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + tintFactor * 20)); // Blue
                data[i + 1] = Math.min(255, Math.max(0, data[i + 1] - tintFactor * 10)); // Green
              } else {
                // Add green (reduce red + blue, increase green)
                data[i + 1] = Math.min(255, Math.max(0, data[i + 1] - tintFactor * 30)); // Green
              }
            }
          }

          tempCtx.putImageData(imageData, 0, 0);

          // Draw the adjusted image
          ctx.filter = 'none'; // Already applied
          ctx.drawImage(tempCanvas, posX, posY);
        } else {
          // Fallback: just draw normally
          ctx.drawImage(background.image, posX, posY, imgWidth, imgHeight);
        }
      } else {
        // No temperature/tint, draw normally
        ctx.drawImage(background.image, posX, posY, imgWidth, imgHeight);
      }

      ctx.restore();
    } else if (background.type === 'gradient') {
      const angleRad = (background.gradientAngle * Math.PI) / 180;
      const centerX = width / 2;
      const centerY = height / 2;
      const x1 = centerX - (Math.cos(angleRad) * width) / 2;
      const y1 = centerY - (Math.sin(angleRad) * height) / 2;
      const x2 = centerX + (Math.cos(angleRad) * width) / 2;
      const y2 = centerY + (Math.sin(angleRad) * height) / 2;

      const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
      gradient.addColorStop(0, background.gradientColor1);
      gradient.addColorStop(1, background.gradientColor2);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.fillStyle = background.color;
      ctx.fillRect(0, 0, width, height);
    }
  }

  /**
   * Draw shadow layer
   */
  drawShadowLayer(
    ctx: CanvasRenderingContext2D,
    shadow: ShadowLayerConfig,
    subjectX: number,
    subjectY: number,
    subjectWidth: number
  ): void {
    if (!shadow.enabled || !shadow.image) return;

    ctx.save();

    const scale = shadow.scale / 100;
    const shadowWidth = subjectWidth * scale;
    const shadowHeight = (shadow.image.height / shadow.image.width) * shadowWidth;

    const shadowX = subjectX + shadow.offsetX;
    const shadowY = subjectY + shadow.offsetY;

    ctx.globalAlpha = shadow.opacity / 100;

    // Apply blur filter if specified
    if (shadow.blur && shadow.blur > 0) {
      ctx.filter = `blur(${shadow.blur}px)`;
    }

    ctx.translate(shadowX, shadowY);
    ctx.rotate((shadow.rotation * Math.PI) / 180);
    ctx.translate(-shadowWidth / 2, -shadowHeight / 2);

    ctx.drawImage(shadow.image, 0, 0, shadowWidth, shadowHeight);

    ctx.restore();
  }

  /**
   * Draw canvas guides (grid, center lines, rule of thirds)
   */
  drawCanvasGuides(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    config: CanvasGuideConfig
  ): void {
    ctx.save();

    // Grid overlay (6x6 grid)
    if (config.showGrid) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);

      const gridSize = Math.min(width, height) / 6;
      for (let i = 1; i < 6; i++) {
        // Vertical lines
        ctx.beginPath();
        ctx.moveTo(i * gridSize, 0);
        ctx.lineTo(i * gridSize, height);
        ctx.stroke();

        // Horizontal lines
        ctx.beginPath();
        ctx.moveTo(0, i * gridSize);
        ctx.lineTo(width, i * gridSize);
        ctx.stroke();
      }
    }

    // Center lines
    if (config.showCenterLines) {
      ctx.strokeStyle = 'rgba(255, 100, 100, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);

      // Vertical center
      ctx.beginPath();
      ctx.moveTo(width / 2, 0);
      ctx.lineTo(width / 2, height);
      ctx.stroke();

      // Horizontal center
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.stroke();
    }

    // Rule of thirds
    if (config.showRuleOfThirds) {
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);

      const thirdW = width / 3;
      const thirdH = height / 3;

      // Vertical lines at 1/3 and 2/3
      ctx.beginPath();
      ctx.moveTo(thirdW, 0);
      ctx.lineTo(thirdW, height);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(2 * thirdW, 0);
      ctx.lineTo(2 * thirdW, height);
      ctx.stroke();

      // Horizontal lines at 1/3 and 2/3
      ctx.beginPath();
      ctx.moveTo(0, thirdH);
      ctx.lineTo(width, thirdH);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, 2 * thirdH);
      ctx.lineTo(width, 2 * thirdH);
      ctx.stroke();

      // Draw intersection points
      ctx.fillStyle = 'rgba(255, 215, 0, 0.8)';
      const intersections = [
        { x: thirdW, y: thirdH },
        { x: 2 * thirdW, y: thirdH },
        { x: thirdW, y: 2 * thirdH },
        { x: 2 * thirdW, y: 2 * thirdH }
      ];
      intersections.forEach(pt => {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    ctx.restore();
  }
}
